import { useTheme } from "@mui/material/styles";
import React from "react";

/**
 * SummaryStats component for rendering statistical summary rows in table headers
 * Displays Sum, Max, Min, and Unique value statistics for each column
 */
const SummaryStats = ({
  showStats,
  tableData,
  calculateColumnStats,
  showPageIndicators = false,
}) => {
  const theme = useTheme();
  const borderColor = theme.brand.gray.gainsboro;

  if (!showStats || !tableData?.headers) {
    return null;
  }

  const commonHeaderStyle = {
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    backgroundColor: "#f9f9f9",
    fontSize: "11px",
    padding: "6px 12px",
    fontWeight: "normal",
    position: "sticky",
    zIndex: 5,
    cursor: "pointer",
    height: "32px",
    minHeight: "32px",
    boxSizing: "border-box",
  };

  return (
    <>
      {/* Sum Row */}
      <tr>
        {/* Empty cell for page indicator column */}
        {showPageIndicators && (
          <th
            style={{
              ...commonHeaderStyle,
              border: `1px solid ${borderColor}`,
              borderBottom: `1px solid ${borderColor}`,
              top: 0,
              left: 0,
              width: "48px",
              backgroundColor: "#f0f0f0",
              zIndex: 6,
            }}
          />
        )}
        {(tableData.headers || []).map((header, colIndex) => {
          const stats = calculateColumnStats(tableData, colIndex);
          return (
            <th
              key={`sum-${colIndex}`}
              style={{
                ...commonHeaderStyle,
                border: `1px solid ${borderColor}`,
                borderBottom: `1px solid ${borderColor}`,
                top: 0,
              }}
            >
              {`Sum: ${stats.sum}`}
            </th>
          );
        })}
      </tr>

      {/* Max Row */}
      <tr>
        {/* Empty cell for page indicator column */}
        {showPageIndicators && (
          <th
            style={{
              ...commonHeaderStyle,
              borderTop: "none",
              borderBottom: `1px solid ${borderColor}`,
              top: "32px",
              left: 0,
              width: "48px",
              backgroundColor: "#f0f0f0",
              zIndex: 6,
            }}
          />
        )}
        {(tableData.headers || []).map((header, colIndex) => {
          const stats = calculateColumnStats(tableData, colIndex);
          return (
            <th
              key={`max-${colIndex}`}
              style={{
                ...commonHeaderStyle,
                borderTop: "none",
                borderBottom: `1px solid ${borderColor}`,
                top: "32px",
              }}
            >
              {`Max: ${stats.max}`}
            </th>
          );
        })}
      </tr>

      {/* Min Row */}
      <tr>
        {/* Empty cell for page indicator column */}
        {showPageIndicators && (
          <th
            style={{
              ...commonHeaderStyle,
              borderTop: "none",
              borderBottom: `1px solid ${borderColor}`,
              top: "64px",
              left: 0,
              width: "48px",
              backgroundColor: "#f0f0f0",
              zIndex: 6,
            }}
          />
        )}
        {(tableData.headers || []).map((header, colIndex) => {
          const stats = calculateColumnStats(tableData, colIndex);
          return (
            <th
              key={`min-${colIndex}`}
              style={{
                ...commonHeaderStyle,
                borderTop: "none",
                borderBottom: `1px solid ${borderColor}`,
                top: "64px",
              }}
            >
              {`Min: ${stats.min}`}
            </th>
          );
        })}
      </tr>

      {/* Unique Row */}
      <tr>
        {/* Empty cell for page indicator column */}
        {showPageIndicators && (
          <th
            style={{
              ...commonHeaderStyle,
              borderTop: "none",
              borderBottom: `2px solid ${borderColor}`,
              top: "96px",
              left: 0,
              width: "48px",
              backgroundColor: "#f0f0f0",
              zIndex: 6,
            }}
          />
        )}
        {(tableData.headers || []).map((header, colIndex) => {
          const stats = calculateColumnStats(tableData, colIndex);
          return (
            <th
              key={`unique-${colIndex}`}
              style={{
                ...commonHeaderStyle,
                borderTop: "none",
                borderBottom: `2px solid ${borderColor}`,
                top: "96px",
              }}
            >
              {`Unique: ${stats.unique}`}
            </th>
          );
        })}
      </tr>
    </>
  );
};

export default SummaryStats;
